import { useContext, useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, Dialog } from "@blueprintjs/core";
import {
  faCheckCircle,
  faCircleNotch,
  faCodeBranch,
  faSave,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HoverCard, Tooltip } from "@mantine/core";

import { Permission } from "@types";

import { WorkbenchContext } from "@pages/editor/providers";

import { LegacyRuleSaveDialog } from "./LegacyRuleSaveDialog";
import { SaveRuleUpsellPopover } from "./SaveRuleUpsellPopover";

/**
 * Button for saving or forking a rule
 * If the user is able to save changes to a rule, the button says "Save"
 * If the user cannot save changes or the user has changed the rule id
 * (thus saving would create a fork), the button says "Fork"
 */
const ActionButton = () => {
  const { workbench } = useContext(WorkbenchContext);
  const {
    bundle,
    remoteBundle,
    isSaving,
    hasUnsavedChanges,
    doesRuleBelongToUser,
    hasRuleNameChanged,
    needsForkingToSave,
  } = workbench;
  const [isLegacyRuleSaveDialogOpen, setIsLegacyRuleSaveDialogOpen] =
    useState(false);

  // can't save without a rule ID
  if (!bundle?.rule?.id) return null;
  // address string has to be valid because that's what we're currently looking at
  if (workbench.addressString === undefined) return null;

  const hasCreatePermission = workbench.permissions.includes(
    Permission.editor_create
  );

  // if saving the saving the current rule would not save changes to the original

  const isLoggedOut = workbench.user === undefined;

  // to be enabled, a user needs to have changes to save, have permission to save,
  // and be logged in
  const isEnabled =
    !isLoggedOut &&
    hasCreatePermission &&
    !isSaving &&
    (hasUnsavedChanges || needsForkingToSave);

  // tooltip content that shows if the user is logged in
  let tooltipContent: JSX.Element | undefined = undefined;
  if (!isSaving && hasRuleNameChanged) {
    tooltipContent = (
      <span>
        Changing the rule name creates a fork - the original rule will be
        unchanged at <code>{remoteBundle?.rule?.id}</code>
      </span>
    );
  } else if (!isSaving && !doesRuleBelongToUser) {
    tooltipContent = (
      <span>
        You cannot modify the original rule at{" "}
        <code>{remoteBundle?.rule?.id}</code>
      </span>
    );
  }

  return (
    <>
      <Dialog
        isOpen={isLegacyRuleSaveDialogOpen}
        onClose={() => setIsLegacyRuleSaveDialogOpen(false)}
      >
        <LegacyRuleSaveDialog
          closeDialog={() => setIsLegacyRuleSaveDialogOpen(false)}
        />
      </Dialog>
      {workbench.user ? (
        <Tooltip label={tooltipContent} position="right" withArrow>
          <div>
            <Button
              onClick={async () => {
                const res = await workbench.saveOrFork();
                if (res?.showSaveLegacy) {
                  setIsLegacyRuleSaveDialogOpen(true);
                }
              }}
              style={{
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
              outlined
              disabled={!isEnabled}
            >
              {isSaving ? (
                <>
                  <FontAwesomeIcon fixedWidth icon={faCircleNotch} spin />{" "}
                  Saving…
                </>
              ) : needsForkingToSave && !isLoggedOut ? (
                <>
                  <FontAwesomeIcon fixedWidth icon={faCodeBranch} /> Fork
                </>
              ) : hasUnsavedChanges ? (
                <>
                  <FontAwesomeIcon fixedWidth icon={faSave} /> Save
                </>
              ) : workbench.isNewBundle ? (
                <>
                  <FontAwesomeIcon fixedWidth icon={faSave} /> Edit to save
                </>
              ) : (
                <>
                  <FontAwesomeIcon fixedWidth icon={faCheckCircle} /> Saved
                </>
              )}
            </Button>
          </div>
        </Tooltip>
      ) : (
        <HoverCard withArrow>
          <HoverCard.Target>
            <div>
              <Button
                style={{
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
                outlined
                disabled
              >
                <FontAwesomeIcon fixedWidth icon={faSave} /> Save
              </Button>
            </div>
          </HoverCard.Target>
          <HoverCard.Dropdown p="md">
            <SaveRuleUpsellPopover />
          </HoverCard.Dropdown>
        </HoverCard>
      )}
    </>
  );
};
export const HeaderActionButton = observer(ActionButton);
